<template>
	<div class="tw-overflow-y-auto" ref="scrollElement" @scroll="onScroll">
		<slot name="loading" v-if="tableStore.loading && reverse">
			<div class="tw-flex tw-flex-1 tw-justify-center">
				<Spinner v-bind="spinnerAttrs" />
			</div>
		</slot>

		<slot v-bind="{ data: tableStore.data, loading: tableStore.loading }"></slot>

		<slot name="loading" v-if="tableStore.loading && !reverse">
			<div class="tw-flex tw-flex-1 tw-justify-center">
				<Spinner v-bind="spinnerAttrs" />
			</div>
		</slot>

		<slot name="no-data" v-if="tableStore.data.length === 0 && !tableStore.loading"></slot>
	</div>
</template>

<script setup>
import { getCurrentInstance, onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue';
import Spinner from './spinner.vue';
import { useTableStore } from '../store/shared/table-store';
import { useFirebaseTableStore } from '../store/shared/firebase-table-store';

// fetchDataFunction must return {data: [], totalRecords: Number}
const props = defineProps({
	limit: {
		type: Number,
		default: 10,
	},
	fetchDataFunction: Function,
	filters: {
		type: Object,
		default: () => ({}),
	},
	offset: {
		type: Number,
		default: 20,
	},
	spinnerAttrs: {
		type: Object,
		default: () => ({}),
	},
	emitData: {
		type: Boolean,
		default: false,
	},
	fetchOnMounted: {
		type: Boolean,
		default: true,
	},
	customStoreName: {
		type: String,
		default: undefined,
	},
	reverse: {
		type: Boolean,
		default: false,
	},
	firebaseStoreData: {
		type: Object,
	},
});

const emit = defineEmits(['input', 'total']);

const vm = getCurrentInstance();
const page = ref(0);
const scrollElement = ref(null);

const tableStoreName = computed(() => {
	if (props.customStoreName) return props.customStoreName;
	return `scrollList-${vm.uid}`;
});

const tableStore = !props.firebaseStoreData
	? useTableStore({
			fetchDataFunction: props.fetchDataFunction,
			tableName: tableStoreName.value,
	  })
	: useFirebaseTableStore({
			tableName: tableStoreName.value,
			...props.firebaseStoreData,
	  });

const emitData = () => {
	emit('input', tableStore.data);
};

const fetchData = async () => {
	if (tableStore.totalRecords <= tableStore.data.length) return false;
	if (tableStore.loading) return false;

	const target = scrollElement.value;
	const lastScrollHeight = target.scrollHeight;

	page.value++;
	try {
		await tableStore.fetchData(
			{
				pagination: {
					page: page.value,
					limit: props.limit,
					pageAction: 'next',
				},
				filters: {
					...props.filters,
				},
				reverse: props.reverse,
			},
			true,
		);
		if (props.emitData) {
			emitData();
		}
		if (props.reverse) {
			handleReverseScroll(lastScrollHeight);
		}
		emit('total', tableStore.totalRecords);
		return true;
	} catch (err) {
		console.error(err);
		return false;
	}
};

const checkFetch = () => {
	const target = scrollElement.value;
	const clientHeight = target.clientHeight;
	const scrollTop = target.scrollTop;
	const scrollHeight = target.scrollHeight;
	if (props.reverse) {
		const rem = scrollTop;
		if (rem <= props.offset) {
			return true;
		}
	} else {
		const rem = scrollHeight - clientHeight - scrollTop;
		if (rem > 0 && rem <= props.offset) {
			return true;
		}
	}
	return false;
};

const handleReverseScroll = (lastScrollHeight) => {
	nextTick(() => {
		const target = scrollElement.value;
		const scrollHeight = target.scrollHeight;
		const diff = scrollHeight - lastScrollHeight;
		target.scrollTop = diff;
	});
};

const scrollToBottom = () => {
	const target = scrollElement.value;
	const scrollHeight = target.scrollHeight;

	target.scrollTo({
		top: scrollHeight,
		left: 0,
		behavior: 'smooth',
	});
};

const onScroll = () => {
	if (checkFetch()) {
		fetchData();
	}
};

const reset = () => {
	page.value = 0;
	tableStore.nextCursor = null;
	tableStore.data = [];
	tableStore.totalRecords = 1;
	// tableStore.loading = false;
	emitData();
};

const fillInitialData = async () => {
	const res = await fetchData();
	if (!res) return;
	if (checkFetch()) {
		fillInitialData();
	}
};

const init = () => {
	reset();
	fillInitialData();
};

onMounted(() => {
	if (!props.fetchOnMounted) return;
	init();
});

onUnmounted(() => {
	tableStore.$reset();
	tableStore.$dispose();
});

watch(
	() => props.filters,
	() => {
		init();
	},
	{
		deep: true,
	},
);

defineExpose({
	init,
	scrollToBottom,
	tableStore,
	fetchData,
});
</script>

<style lang="scss" scoped></style>
